import React, { useCallback, useEffect, useMemo, useState } from "react";
import AxiosInstance from "../../AxiosInstance";
import moment from "moment";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { handleAuth } from "../../../components/Login/Auth";
import "../style.css";
import "../CustomerContractJobber.css";
import { Box, Button as MuiButton, Typography, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  JobberListCard,
  JobberPagination,
  JobberSearch,
  JobberSorting,
  JobberStatusBadge,
  JobberTable,
  JobberTableDocRefTitleCell,
  getJobberStatusTone,
} from "../../../components/MuiTable/index.jsx";
import CustomerTitleWrapCell from "../CustomerTitleWrapCell";
import { useUserType } from "../../../contexts/UserTypeContext.jsx";

/** Pastel View — mobile collapse Actions row; desktop column hidden via CSS */
const CONTRACT_VIEW_BG = "#E0E7FF";
const CONTRACT_VIEW_COLOR = "#1E3A8A";

const contractMobileActionBtnBase = {
  width: "100%",
  minWidth: 0,
  alignSelf: "stretch",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "8px",
  minHeight: 32,
  px: 0.5,
  py: 0.375,
  lineHeight: 1.15,
  boxShadow: "none",
  whiteSpace: "nowrap",
  justifyContent: "center",
  "& .MuiButton-startIcon": { mr: 0.25, ml: -0.1 },
};

function formatUsd(value) {
  const n = Number(value);
  if (value == null || value === "" || Number.isNaN(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function contractTotalNumeric(c) {
  const n = Number(c?.Total);
  return Number.isNaN(n) ? 0 : n;
}

const CONTRACT_BUCKET_KEYS = ["Today", "Upcoming", "Scheduled", "Converted", "UnScheduled"];

function mergeContractBuckets(data) {
  const map = new Map();
  const add = (c) => {
    const id = c?.ContractId;
    if (id == null) return;
    if (!map.has(id)) map.set(id, c);
  };
  CONTRACT_BUCKET_KEYS.forEach((k) => (data?.[k] || []).forEach(add));
  return Array.from(map.values());
}

function contractMatchesStatusFilter(row, filterLabel) {
  if (!filterLabel || filterLabel === "All") return true;
  const st = String(row?.Status ?? "").trim();
  if (filterLabel === "Unscheduled") return st === "Unscheduled" || st === "UnScheduled";
  return st === filterLabel;
}

const CUSTOMER_CONTRACT_STATUS_ITEMS = [
  { text: "All" },
  { text: "Today" },
  { text: "Upcoming" },
  { text: "Scheduled" },
  { text: "Converted" },
  { text: "Unscheduled" },
];

function formatContractAddress(address) {
  if (!address) return "";
  const addr = address?.Address || "";
  const city = address?.City || "";
  const state = address?.State || "";
  const zip = address?.Zip || "";
  return [addr, city, state, zip].filter(Boolean).join(", ");
}

function jobDescriptionContract(c) {
  const t = c?.Title || c?.Description || c?.Subject;
  return (t && String(t).trim()) || "—";
}

function TwoLineCell({ text }) {
  const s = text == null || text === "" ? "—" : String(text);
  return (
    <Typography
      component="div"
      title={s}
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        wordBreak: "break-word",
        lineHeight: 1.35,
        maxWidth: 280,
        fontSize: "0.9375rem",
        color: "text.primary",
      }}
    >
      {s}
    </Typography>
  );
}

/** Same pills as admin contract list (`JobberStatusBadge` + `getJobberStatusTone`). */
function contractStatusBadge(statusRaw) {
  const s = String(statusRaw || "").trim();
  const label = s === "UnScheduled" ? "Unscheduled" : s || "—";
  return (
    <JobberStatusBadge tone={getJobberStatusTone(statusRaw)}>
      {label}
    </JobberStatusBadge>
  );
}

function contractStatusRank(status) {
  const s = String(status || "");
  const order = ["Today", "Upcoming", "Scheduled", "Converted", "Unscheduled", "UnScheduled"];
  const idx = order.indexOf(s);
  return idx >= 0 ? idx : 50;
}

function contractPrimarySortTime(c) {
  const t = moment(c?.createdAt);
  if (t.isValid()) return t.valueOf();
  const u = moment(c?.updatedAt);
  return u.isValid() ? u.valueOf() : 0;
}

function compareContracts(a, b, field, order) {
  const mul = order === "asc" ? 1 : -1;
  switch (field) {
    case "createdAt": {
      const va = contractPrimarySortTime(a);
      const vb = contractPrimarySortTime(b);
      if (va !== vb) return (va - vb) * mul;
      const na = parseInt(a?.ContractNumber, 10) || 0;
      const nb = parseInt(b?.ContractNumber, 10) || 0;
      return (na - nb) * mul;
    }
    case "ContractNumber": {
      const va = parseInt(a?.ContractNumber, 10) || 0;
      const vb = parseInt(b?.ContractNumber, 10) || 0;
      return (va - vb) * mul;
    }
    case "CompanyName": {
      const va = (a?._companyLabel || "").toLowerCase();
      const vb = (b?._companyLabel || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Property": {
      const va = (formatContractAddress(a?.address) || "").toLowerCase();
      const vb = (formatContractAddress(b?.address) || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Total": {
      const va = contractTotalNumeric(a);
      const vb = contractTotalNumeric(b);
      if (va !== vb) return (va - vb) * mul;
      return 0;
    }
    case "Title": {
      const va = (jobDescriptionContract(a) || "").toLowerCase();
      const vb = (jobDescriptionContract(b) || "").toLowerCase();
      return va.localeCompare(vb) * mul;
    }
    case "Status": {
      const ra = contractStatusRank(a?.Status);
      const rb = contractStatusRank(b?.Status);
      if (ra !== rb) return (ra - rb) * mul;
      return (contractPrimarySortTime(a) - contractPrimarySortTime(b)) * mul;
    }
    default:
      return 0;
  }
}

const CONTRACT_HEADER_DATA = [
  { label: "Contract #", field: "ContractNumber", wrap: true },
  { label: "Property", field: "Property", wrap: true },
  { label: "Created On", field: "createdAt" },
  { label: "Status", field: "Status" },
  { label: "Total", field: "Total" },
  { label: "Actions", field: "" },
];

function CustomerContractList() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  const [tokenDecode, setTokenDecode] = useState({});
  const [quotes, setQuotes] = useState({
    Today: [],
    Upcoming: [],
    Scheduled: [],
    Converted: [],
    UnScheduled: [],
  });
  const [loader, setLoader] = useState(true);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
  const [listSearch, setListSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  const handleStatusSelect = useCallback((text) => {
    setStatusFilter(text || "All");
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const auth = await handleAuth(navigate, location);
      setTokenDecode(auth?.data ?? {});
      setDateDecode(auth?.themes?.Format || "MM/DD/YYYY");
    } catch (error) {
      console.error("Error fetching token decode data:", error);
      setDateDecode("MM/DD/YYYY");
    } finally {
      setIsAuthResolved(true);
    }
  }, [navigate, location]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const CustomerId = tokenDecode?.UserId;
  const companyLabel =
    (tokenDecode?.CompanyName && String(tokenDecode.CompanyName).trim()) ||
    (tokenDecode?.BusinessName && String(tokenDecode.BusinessName).trim()) ||
    "";

  useEffect(() => {
    const fetchContracts = async () => {
      // Wait until auth resolution; otherwise loader can hide too early
      // and skip the intended table skeleton state.
      if (!isAuthResolved) return;

      if (!CustomerId) {
        setLoader(false);
        return;
      }
      setLoader(true);
      try {
        const response = await AxiosInstance.get(`/v1/contract/contracts/${CustomerId}`);
        if (response?.data?.statusCode === 200 || response?.data?.statusCode === 204) {
          setQuotes(response?.data?.data || {});
        } else {
          setQuotes({
            Today: [],
            Upcoming: [],
            Scheduled: [],
            Converted: [],
            UnScheduled: [],
          });
        }
      } catch (err) {
        console.error(err?.message);
        setQuotes({
          Today: [],
          Upcoming: [],
          Scheduled: [],
          Converted: [],
          UnScheduled: [],
        });
      } finally {
        setLoader(false);
      }
    };
    fetchContracts();
  }, [CustomerId, isAuthResolved]);

  useEffect(() => {
    setPage(0);
  }, [listSearch, statusFilter]);

  const merged = useMemo(() => {
    const rows = mergeContractBuckets(quotes);
    return rows.map((c) => ({
      ...c,
      _companyLabel: companyLabel || "—",
    }));
  }, [quotes, companyLabel]);

  const statusFiltered = useMemo(
    () => merged.filter((c) => contractMatchesStatusFilter(c, statusFilter)),
    [merged, statusFilter]
  );

  const contractMatchesSearch = useCallback((c, rawQuery) => {
    const q = (rawQuery || "").trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      String(c?.ContractNumber ?? ""),
      c?.Title ?? "",
      c?.Description ?? "",
      jobDescriptionContract(c),
      companyLabel,
      formatContractAddress(c?.address),
      c?.Total != null ? String(c.Total) : "",
      String(c?.Status ?? ""),
    ]
      .join(" ")
      .toLowerCase();
    return q
      .split(/\s+/)
      .filter(Boolean)
      .every((w) => haystack.includes(w));
  }, [companyLabel]);

  const filtered = useMemo(
    () => statusFiltered.filter((c) => contractMatchesSearch(c, listSearch)),
    [statusFiltered, listSearch, contractMatchesSearch]
  );

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => compareContracts(a, b, sortField, sortOrder));
    return list;
  }, [filtered, sortField, sortOrder]);

  const countData = sorted.length;

  const pageSlice = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(sorted.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [sorted.length, rowsPerPage, page]);

  const baseUrl = CompanyUrl || "defaultcompany";
  const navigatePath = `/${baseUrl}/customers/contract-details`;
  const navigats = location?.state?.navigats
    ? [...location.state.navigats, "/contract-details"]
    : ["/contract-details"];

  const handleViewContract = useCallback(
    (contractId) => {
      if (contractId == null) return;
      const newPath = navigatePath.split("/");
      navigate(navigatePath, {
        state: {
          id: contractId,
          navigats: [...navigats, `/${newPath[newPath.length - 1]}`],
        },
      });
    },
    [navigatePath, navigats, navigate]
  );

  const goToContractDetail = useCallback(
    (e, contractId) => {
      if (e) e.stopPropagation();
      handleViewContract(contractId);
    },
    [handleViewContract]
  );

  const cellData = useMemo(
    () =>
      pageSlice.map((c) => {
        const createdOnDisplay = formatDateWithUserTimezone(c?.createdAt, {
          dateFormat: typeof DateDecode === "string" ? DateDecode : "MM/DD/YYYY",
          includeSeconds: false,
        });
        const num = c?.ContractNumber;
        const jobText = jobDescriptionContract(c);
        const propertyText = formatContractAddress(c?.address) || "—";
        const cid = c?.ContractId;
        const contractLinkColor = isDonationMode
          ? "#29B3E2"
          : isDark
            ? theme.palette.primary.light
            : "#2563EB";
        return {
          key: cid,
          value: [
            <JobberTableDocRefTitleCell
              key={`doc-c-${cid}`}
              refNumber={num}
              onRefClick={(e) => goToContractDetail(e, cid)}
              linkColor={contractLinkColor}
              title={jobText}
              titleLineBreakStyle="none"
              wordsPerLine={0}
            />,
            <CustomerTitleWrapCell key={`prop-${cid}`} text={propertyText} />,
            <Typography
              key={`co-${cid}`}
              sx={{
                whiteSpace: "nowrap",
                fontSize: "0.9375rem",
                color: "text.secondary",
              }}
            >
              {createdOnDisplay || "—"}
            </Typography>,
            contractStatusBadge(c?.Status),
            <Typography
              key={`tot-${cid}`}
              sx={{
                fontWeight: 700,
                fontSize: "0.9375rem",
                color: "text.primary",
              }}
            >
              {formatUsd(c?.Total)}
            </Typography>,
            <Box
              key={`act-${cid}`}
              className="customer-contract-mobile-actions customer-list-actions-mobile"
              sx={{ display: "grid", width: "100%", maxWidth: "100%" }}
            >
              <MuiButton
                type="button"
                size="small"
                variant="contained"
                disableElevation
                fullWidth
                startIcon={
                  <VisibilityOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: isDonationMode ? "#0f766e" : CONTRACT_VIEW_COLOR,
                    }}
                  />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewContract(cid);
                }}
                sx={{
                  ...contractMobileActionBtnBase,
                  ...(isDark && !isDonationMode
                    ? {
                        backgroundColor: "rgba(96, 165, 250, 0.12)",
                        color: theme.palette.primary.light,
                        border: "1px solid rgba(96, 165, 250, 0.35)",
                        "&:hover": {
                          backgroundColor: "rgba(96, 165, 250, 0.2)",
                          borderColor: "rgba(96, 165, 250, 0.5)",
                        },
                      }
                    : {
                        backgroundColor: isDonationMode
                          ? "rgba(104, 178, 203, 0.2)"
                          : CONTRACT_VIEW_BG,
                        color: isDonationMode ? "#0f766e" : CONTRACT_VIEW_COLOR,
                        border: isDonationMode
                          ? "1px solid rgba(41, 179, 226, 0.3)"
                          : "1px solid rgba(30, 58, 138, 0.2)",
                        "&:hover": {
                          backgroundColor: isDonationMode
                            ? "rgba(104, 178, 203, 0.32)"
                            : "#c7d2fe",
                          borderColor: isDonationMode
                            ? "rgba(41, 179, 226, 0.45)"
                            : "rgba(30, 58, 138, 0.32)",
                        },
                      }),
                }}
              >
                View
              </MuiButton>
            </Box>,
          ],
        };
      }),
    [
      pageSlice,
      DateDecode,
      formatDateWithUserTimezone,
      isDonationMode,
      goToContractDetail,
      isDark,
      theme,
    ]
  );

  const NoSearchResults = () => (
    <Box
      sx={{
        textAlign: "center",
        padding: "48px 20px",
        borderRadius: "16px",
        marginTop: "24px",
        background: isDark ? "rgba(255,255,255,0.04)" : "#f8f9fa",
        border: isDark
          ? "2px dashed rgba(255,255,255,0.12)"
          : "2px dashed #e1e5eb",
      }}
    >
      <SearchIcon
        sx={{
          fontSize: 56,
          color: isDark ? "primary.light" : "#0f2b46",
          marginBottom: 2,
          opacity: 0.55,
        }}
      />
      <Typography
        variant="h6"
        gutterBottom
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        No contracts match your search
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", maxWidth: "400px", margin: "0 auto" }}
      >
        Try a different contract #, title, address, amount, or status.
      </Typography>
    </Box>
  );

  const NoContracts = () => (
    <Box
      sx={{
        textAlign: "center",
        padding: "80px 20px",
        borderRadius: "16px",
        marginTop: "24px",
        background: isDark ? "rgba(255,255,255,0.04)" : "#f8f9fa",
        border: isDark
          ? "2px dashed rgba(255,255,255,0.12)"
          : "2px dashed #e1e5eb",
      }}
    >
      <InfoOutlinedIcon
        sx={{ fontSize: 100, color: "var(--amber)", marginBottom: 3, opacity: 0.8 }}
      />
      <Typography
        variant="h5"
        gutterBottom
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        No Contracts Available
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: "text.secondary", maxWidth: "400px", margin: "0 auto" }}
      >
        You haven&apos;t received any contracts yet. Once available, they will show up here.
      </Typography>
    </Box>
  );

  const emptyAll = merged.length === 0;
  const emptyAfterSearch =
    !emptyAll && filtered.length === 0 && (listSearch || "").trim().length > 0;
  const emptyAfterStatusOnly =
    !emptyAll && filtered.length === 0 && !(listSearch || "").trim().length;

  const contractsToolbar = (
    <Box className="quotesearch gap-2 d-flex customer_searchBar searchBarOfTable">
      <JobberSorting
        triggerVariant="light"
        dropdownItems={CUSTOMER_CONTRACT_STATUS_ITEMS}
        placeholder="Select Status"
        value={statusFilter}
        onSelect={handleStatusSelect}
      />
      <JobberSearch search={listSearch} setSearch={setListSearch} />
    </Box>
  );

  const contractsFooter = (
    <JobberPagination
      totalData={countData}
      currentData={rowsPerPage}
      dataPerPage={rowsPerPage}
      pageItems={[10, 25, 50]}
      page={page}
      setPage={setPage}
      setRowsPerPage={setRowsPerPage}
    />
  );

  const jobberContractMobileProps = {
    showMobileViewDetailsButton: false,
    actionColumnIndex: 5,
    mobileCardPrimaryColumnIndex: 0,
    mobileCardSubtitleColumnIndex: 2,
    mobileCardStatusColumnIndex: 3,
  };

  return (
    <Box
      className="customer-contract-jobber-wrap"
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" }}
    >
      {loader ? (
        <JobberListCard
          title="Your Contracts"
          totalCount={0}
          addButton={null}
          toolbar={contractsToolbar}
          loader={loader}
          footer={contractsFooter}
        >
          <JobberTable
            headerData={CONTRACT_HEADER_DATA}
            cellData={[]}
            {...jobberContractMobileProps}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
            sortField={sortField}
            isCollapse={false}
            isNavigate
            navigatePath={navigatePath}
            navigats={navigats}
          />
        </JobberListCard>
      ) : emptyAll ? (
        <NoContracts />
      ) : emptyAfterSearch ? (
        <NoSearchResults />
      ) : (
        <JobberListCard
          title="Your Contracts"
          totalCount={countData}
          addButton={null}
          toolbar={contractsToolbar}
          loader={false}
          footer={contractsFooter}
        >
          {emptyAfterStatusOnly ? (
            <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: "text.primary" }}
              >
                No contracts for the selected status
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                Choose &quot;All&quot; or another status to see more contracts.
              </Typography>
            </Box>
          ) : (
            <JobberTable
              headerData={CONTRACT_HEADER_DATA}
              cellData={cellData}
              {...jobberContractMobileProps}
              setSortField={setSortField}
              setSortOrder={setSortOrder}
              sortOrder={sortOrder}
              sortField={sortField}
              isCollapse={false}
              isNavigate
              navigatePath={navigatePath}
              navigats={navigats}
            />
          )}
        </JobberListCard>
      )}
    </Box>
  );
}

export default CustomerContractList;
